import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Moon, Sun, Monitor, Database } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { apiClient } from '../../utils/api';
import { toast } from 'sonner';

export function GeneralSettings() {
  const { theme, setTheme } = useTheme();
  const [currency, setCurrency] = React.useState('USD');
  const [language, setLanguage] = React.useState('en');
  const [autoRefresh, setAutoRefresh] = React.useState(true);
  const [refreshInterval, setRefreshInterval] = React.useState('30');
  const [mockMode, setMockMode] = React.useState(apiClient.isMockMode());

  const handleMockModeToggle = (enabled: boolean) => {
    setMockMode(enabled);
    apiClient.setMockMode(enabled);
    toast.success(
      enabled 
        ? 'Mock mode enabled - Using simulated data' 
        : 'Mock mode disabled - Using real API'
    );
    // Reload page to apply changes
    setTimeout(() => window.location.reload(), 500);
  };

  return (
    <div className="space-y-6">
      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Theme</Label>
            <div className="grid grid-cols-3 gap-4">
              <Button
                variant={theme === 'light' ? 'default' : 'outline'}
                onClick={() => setTheme('light')}
                className="flex items-center gap-2"
              >
                <Sun className="h-4 w-4" />
                Light
              </Button>
              <Button
                variant={theme === 'dark' ? 'default' : 'outline'}
                onClick={() => setTheme('dark')}
                className="flex items-center gap-2"
              >
                <Moon className="h-4 w-4" />
                Dark
              </Button>
              <Button
                variant={theme === 'system' ? 'default' : 'outline'}
                onClick={() => setTheme('system')}
                className="flex items-center gap-2"
              >
                <Monitor className="h-4 w-4" />
                System
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Currency & Language */}
      <Card>
        <CardHeader>
          <CardTitle>Localization</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Default Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                  <SelectItem value="JPY">JPY (¥)</SelectItem>
                  <SelectItem value="BTC">BTC (₿)</SelectItem>
                  <SelectItem value="ETH">ETH (Ξ)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="es">Español</SelectItem>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="de">Deutsch</SelectItem>
                  <SelectItem value="zh">中文</SelectItem>
                  <SelectItem value="ja">日本語</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data & Updates */}
      <Card>
        <CardHeader>
          <CardTitle>Data & Updates</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Auto-refresh Data</Label>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Automatically update prices and balances
              </div>
            </div>
            <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh} />
          </div>

          {autoRefresh && (
            <div className="space-y-2">
              <Label>Refresh Interval (seconds)</Label>
              <Input
                type="number"
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(e.target.value)}
                min="10"
                max="300"
                className="w-32"
              />
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Lower values use more bandwidth
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Advanced */}
      <Card>
        <CardHeader>
          <CardTitle>Advanced</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Developer Mode</Label>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Show technical details and debugging information
              </div>
            </div>
            <Switch />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Experimental Features</Label>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Enable beta features (may be unstable)
              </div>
            </div>
            <Switch />
          </div>

          <div className="pt-4 border-t">
            <Button variant="outline" className="w-full">
              Reset All Settings to Default
            </Button>
          </div>

          <div className="pt-4 border-t">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5 flex-1">
                <div className="flex items-center gap-2">
                  <Database className="h-4 w-4 text-blue-500" />
                  <Label>Mock Mode (Demo)</Label>
                </div>
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  Usar datos simulados en lugar de conexión real a Supabase.
                  {mockMode && <span className="text-blue-500"> (Actualmente activo)</span>}
                </div>
              </div>
              <Switch checked={mockMode} onCheckedChange={handleMockModeToggle} />
            </div>
            {mockMode && (
              <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  💡 <strong>Modo Demo:</strong> La aplicación está usando datos de prueba. 
                  Desactiva esta opción cuando las Edge Functions de Supabase estén desplegadas.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}